/*
  m365-preorder.js — buy button + dynamic checkout for pre-order variants (Step 2).

  WHY THIS IS JS AND NOT LIQUID. On a variant change, Dawn 15's product-info.js swaps
  only five elements out of the fetched HTML — price, Sku, Inventory, Volume,
  Price-Per-Item. The buy button is NOT among them. Instead it reads the `disabled`
  attribute off the fetched button and calls:

      productForm.toggleSubmitButton(disabled, window.variantStrings.soldOut)

  and toggleSubmitButton, when it ENABLES a button, hard-sets the label back to
  window.variantStrings.addToCart. So any label we render in Liquid survives exactly
  until the first variant change. The dynamic-checkout block is not re-rendered either.

  WHERE WE HOOK IN. product-info.js publishes PUB_SUB_EVENTS.variantChange immediately
  AFTER that toggleSubmitButton call, with the fetched html and the new variant. A
  subscriber therefore always runs after Dawn has had its say, which is exactly the
  ordering we need — we re-apply, we never fight it. Nothing in product-info.js or
  product-form.js is patched.

  THE DATA. Per-variant state comes from the JSON island rendered by
  snippets/m365-preorder-state.liquid, so the JS and the Liquid cannot disagree about
  what counts as a pre-order. A variant missing from the island, or a product with no
  island at all, is simply left to Dawn — this script only ever adds behaviour to
  variants it has been told about.
*/

(function () {
  'use strict';

  var LABEL_PREORDER = 'Pre-order now';
  var PROPERTY_NAME = 'properties[Pre-order]';
  var ISO_PROPERTY_NAME = 'properties[_preorder_ships_on]';
  var LABEL_SOLD_OUT = 'Sold out';
  var HIDDEN_CLASS = 'm365-hidden';

  function parseIsland(root) {
    var el = root.querySelector('script[type="application/json"][id^="M365PreorderData-"]');
    if (!el) return null;
    try {
      var list = JSON.parse(el.textContent);
      var byId = {};
      list.forEach(function (row) {
        byId[String(row.id)] = row;
      });
      return byId;
    } catch (e) {
      // A malformed island must not take the PDP down with it. Dawn's own behaviour
      // is the safe fallback, so say so once and stand down.
      console.warn('[m365-preorder] could not parse the pre-order data island', e);
      return null;
    }
  }

  function currentVariantId(root, fromEvent) {
    if (fromEvent) return String(fromEvent);
    var input = root.querySelector('form[action*="/cart/add"] [name="id"]');
    return input ? String(input.value) : null;
  }

  function apply(root, variantIdFromEvent) {
    var byId = parseIsland(root);
    if (!byId) return;

    var id = currentVariantId(root, variantIdFromEvent);
    var state = id ? byId[id] : null;

    syncProperty(root, state);

    var button = root.querySelector('.product-form__submit');
    var label = button ? button.querySelector('span') : null;
    var payment = root.querySelector('.shopify-payment-button');

    if (!button || !label) return;

    if (state && state.sold_out) {
      // The batch is gone. Shopify still considers an oversellable variant available,
      // so Dawn will not have disabled this for us — we have to.
      button.setAttribute('disabled', 'disabled');
      label.textContent = LABEL_SOLD_OUT;
      if (payment) payment.classList.add(HIDDEN_CLASS);
      return;
    }

    if (state && state.preorder) {
      // Re-apply AFTER toggleSubmitButton, which has just reset the label to
      // "Add to cart" if it enabled the button.
      button.removeAttribute('disabled');
      label.textContent = LABEL_PREORDER;
      // Express checkout skips the cart entirely, so the shopper would never see the
      // pre-order messaging that lives there. Hiding it is the point, not a side
      // effect: a pre-order bought through Shop Pay is a pre-order nobody was told
      // about.
      if (payment) payment.classList.add(HIDDEN_CLASS);
      return;
    }

    // Not a pre-order variant: leave Dawn's own state alone, but undo anything we did
    // on a previous variant. Without this the express buttons would stay hidden after
    // switching from a pre-order size to an in-stock one.
    if (payment) payment.classList.remove(HIDDEN_CLASS);
  }

  /*
    Step 4. The add-to-cart form is not re-rendered on a variant change either, so the
    property would otherwise carry the FIRST variant's ships-by date into the cart for
    every size. It is added, updated and — importantly — REMOVED here, so switching
    from a pre-order size to an in-stock one does not quietly tag a normal line item.
  */
  function syncProperty(root, state) {
    var form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    var existing = form.querySelector('[data-m365-preorder-property]');

    var iso = form.querySelector('[data-m365-preorder-iso]');

    if (!state || !(state.preorder || state.sold_out) || !state.ships) {
      if (existing) existing.remove();
      if (iso) iso.remove();
      return;
    }

    if (!existing) {
      existing = document.createElement('input');
      existing.type = 'hidden';
      existing.setAttribute('data-m365-preorder-property', '');
      // No leading underscore: Shopify hides _-prefixed properties from the cart, the
      // checkout and the order, and this one has to be visible at all three.
      existing.name = PROPERTY_NAME;
      form.appendChild(existing);
    }
    existing.value = state.ships;

    // The _-prefixed twin, hidden from the customer, used by the cart banner to rank
    // several pre-order lines by real date instead of parsing the wording back.
    if (state.ships_on) {
      if (!iso) {
        iso = document.createElement('input');
        iso.type = 'hidden';
        iso.setAttribute('data-m365-preorder-iso', '');
        iso.name = ISO_PROPERTY_NAME;
        form.appendChild(iso);
      }
      iso.value = state.ships_on;
    } else if (iso) {
      iso.remove();
    }
  }

  /*
    Step 3. The variant picker is NOT one of the five elements product-info.js swaps,
    so its markers would freeze after the first change on a two-option product. Rather
    than recompute state in the browser from a variant matrix we do not have, we read
    it out of the HTML Dawn has just fetched for us — the same server-rendered truth,
    copied across by option-value id.
  */
  function syncPickerMarkers(root, html) {
    if (!html) return;
    root.querySelectorAll('[data-option-value-id][data-m365-state]').forEach(function (live) {
      var id = live.getAttribute('data-option-value-id');
      var fresh = html.querySelector('[data-option-value-id="' + id + '"][data-m365-state]');
      if (!fresh) return;
      var state = fresh.getAttribute('data-m365-state');
      if (live.getAttribute('data-m365-state') === state) return;
      live.setAttribute('data-m365-state', state);

      var label = live.parentElement && live.parentElement.querySelector('label[for="' + live.id + '"]');
      var freshLabel = html.querySelector('label[for="' + live.id + '"]');
      if (label && freshLabel) {
        var mark = label.querySelector('.m365-size-mark');
        if (mark) mark.remove();
        var freshMark = freshLabel.querySelector('.m365-size-mark');
        if (freshMark) label.insertBefore(freshMark.cloneNode(true), label.firstChild.nextSibling);
      }
    });
  }

  function roots() {
    return document.querySelectorAll('product-info');
  }

  function applyAll(variantId) {
    roots().forEach(function (root) {
      apply(root, variantId);
    });
  }

  function init() {
    applyAll(null);

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      // pubsub.js is loaded ahead of this file in theme.liquid, so this should not
      // happen — but a missing hook would silently mean no label updates, and silence
      // is the failure mode worth naming.
      console.warn('[m365-preorder] pubsub unavailable; labels will not update on variant change');
      return;
    }

    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var data = (event && event.data) || {};
      var variantId = data.variant && data.variant.id;
      var root = data.sectionId
        ? document.querySelector('product-info[data-section="' + data.sectionId + '"]')
        : null;
      if (root) {
        apply(root, variantId);
        syncPickerMarkers(root, data.html);
      } else {
        applyAll(variantId);
        roots().forEach(function (r) {
          syncPickerMarkers(r, data.html);
        });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
